import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  alpha,
  IconButton,
  Tooltip,
  Stack,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  BusinessCenter as BusinessCenterIcon,
  Schedule as ScheduleIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  Phone as PhoneIcon,
  Flag as FlagIcon,
  Info as InfoIcon,
  ConfirmationNumber as TicketIcon,
  AccountCircle as AccountCircleIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import { buscarDetalhesSenha } from '../../services/relatoriosApi';

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.locale('pt-br');

const ModalDetalhesSenha = ({ open, onClose, senha, customTheme }) => {
  const [senhaDetalhada, setSenhaDetalhada] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  // Buscar detalhes completos da senha quando o modal abrir
  useEffect(() => {
    if (open && senha?.id) {
      buscarDetalhesCompletos();
    }
  }, [open, senha?.id]);

  const buscarDetalhesCompletos = async () => {
    try {
      setCarregando(true);
      setErro(null);
      
      const response = await buscarDetalhesSenha(senha.id);
      
      if (response.success) {
        setSenhaDetalhada(response.data);
      } else {
        setErro(response.message || 'Erro ao buscar detalhes da senha');
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes da senha:', error);
      setErro('Erro ao conectar com o servidor');
    } finally {
      setCarregando(false);
    }
  };

  // Usar senha detalhada se disponível, senão usar a senha básica
  const senhaParaExibir = senhaDetalhada || senha;

  if (!senha) return null;

  // Função para formatar data/hora
  const formatarDataHora = (dataHora) => {
    if (!dataHora) return '-';
    return dayjs(dataHora).format('DD/MM/YYYY HH:mm:ss');
  };

  // Função para calcular duração entre duas datas
  const calcularDuracao = (inicio, fim) => {
    if (!inicio || !fim) return null;
    const duracao = dayjs(fim).diff(dayjs(inicio));
    const duracaoObj = dayjs.duration(duracao);
    
    if (duracao < 60000) { // Menos de 1 minuto
      return `${duracaoObj.seconds()}s`;
    } else if (duracao < 3600000) { // Menos de 1 hora
      return `${duracaoObj.minutes()}min ${duracaoObj.seconds()}s`;
    } else {
      return `${duracaoObj.hours()}h ${duracaoObj.minutes()}min`;
    }
  };

  // Função para obter cor do status
  const getStatusColor = (status) => {
    const cores = {
      'AGUARDANDO': '#2196f3',
      'CHAMADA': '#ff9800',
      'EM_ATENDIMENTO': '#9c27b0',
      'FINALIZADA': '#4caf50',
      'CANCELADA': '#f44336',
      'NAO_COMPARECEU': '#795548',
      'REABERTA': '#607d8b'
    };
    return cores[status] || '#757575';
  };

  // Função para traduzir status
  const traduzirStatus = (status) => {
    const traducoes = {
      'AGUARDANDO': 'Aguardando',
      'CHAMADA': 'Chamada',
      'EM_ATENDIMENTO': 'Em Atendimento',
      'FINALIZADA': 'Finalizada',
      'CANCELADA': 'Cancelada',
      'NAO_COMPARECEU': 'Não Compareceu',
      'REABERTA': 'Reaberta'
    };
    return traducoes[status] || status;
  };

  // Função para formatar motivo de emissão
  const formatarMotivoEmissao = (senha) => {
    // Se temos o nome do motivo da tabela, usar ele
    if (senha.motivo_emissao_nome) {
      return senha.motivo_emissao_nome;
    }
    
    // Fallback para os motivos hardcodados antigos
    if (senha.motivo_emissao) {
      const motivos = {
        'cliente_sem_senha': 'Cliente sem senha e fila zerada',
        'servico_sem_cliente': 'Serviço realizado sem cliente',
        'outro': 'Outro'
      };
      return motivos[senha.motivo_emissao] || senha.motivo_emissao;
    }
    
    return null;
  };

  // Construir timeline da senha
  const construirTimeline = () => {
    const eventos = [];

    // Evento 1: Emissão
    if (senhaParaExibir.emitida_em) {
      // Verificar se foi emitida por usuário
      if (senhaParaExibir.usuario_emitiu) {
        const motivoTexto = formatarMotivoEmissao(senhaParaExibir);
        let descricaoMotivo = 'Senha emitida diretamente pelo usuário do sistema';
        
        if (motivoTexto) {
          // Se é "Outro" e tem justificativa, mostrar a justificativa
          if ((senhaParaExibir.motivo_emissao_requer_justificativa || senhaParaExibir.motivo_emissao === 'outro') && senhaParaExibir.outro_motivo) {
            descricaoMotivo = `Motivo: ${senhaParaExibir.outro_motivo}`;
          } else {
            descricaoMotivo = `Motivo: ${motivoTexto}`;
          }
        }
        
        eventos.push({
          label: 'Senha Emitida Pelo Usuário',
          timestamp: senhaParaExibir.emitida_em,
          icon: <AccountCircleIcon />,
          color: '#2196f3',
          descricao: descricaoMotivo,
          isUsuarioEmissao: true
        });
      } else {
        eventos.push({
          label: 'Senha Emitida',
          timestamp: senhaParaExibir.emitida_em,
          icon: <FlagIcon />,
          color: '#2196f3',
          descricao: 'Cliente retirou a senha e entrou na fila de espera'
        });
      }
    }

    // Evento 2: Chamada
    if (senha.chamada_em) {
      const tempoEspera = calcularDuracao(senha.emitida_em, senha.chamada_em);
      eventos.push({
        label: 'Senha Chamada',
        timestamp: senha.chamada_em,
        icon: <PhoneIcon />,
        color: '#ff9800',
        descricao: `Cliente foi chamado para atendimento${tempoEspera ? ` (aguardou ${tempoEspera})` : ''}`
      });
    }

    // Evento 3: Início do Atendimento
    if (senha.inicio_atendimento) {
      const tempoDeslocamento = calcularDuracao(senha.chamada_em, senha.inicio_atendimento);
      eventos.push({
        label: 'Atendimento Iniciado',
        timestamp: senha.inicio_atendimento,
        icon: <PlayArrowIcon />,
        color: '#9c27b0',
        descricao: `Atendimento foi iniciado${senha.usuario_nome ? ` por ${senha.usuario_nome}` : ''}${tempoDeslocamento ? ` (deslocamento: ${tempoDeslocamento})` : ''}`
      });
    }

    // Evento 4: Finalização
    if (senha.finalizada_em) {
      const tempoAtendimento = calcularDuracao(senha.inicio_atendimento, senha.finalizada_em);
      const icon = senha.status === 'FINALIZADA' ? <CheckCircleIcon /> : <CancelIcon />;
      const color = senha.status === 'FINALIZADA' ? '#4caf50' : '#f44336';
      
      eventos.push({
        label: senha.status === 'FINALIZADA' ? 'Atendimento Finalizado' : 'Atendimento Cancelado',
        timestamp: senha.finalizada_em,
        icon,
        color,
        descricao: `Atendimento foi ${senha.status === 'FINALIZADA' ? 'concluído com sucesso' : 'cancelado'}${tempoAtendimento ? ` (duração: ${tempoAtendimento})` : ''}`
      });
    }

    return eventos;
  };

  // Calcular métricas da senha
  const calcularMetricas = () => {
    const metricas = [];

    // Tempo de Espera (Emissão → Chamada)
    if (senha.emitida_em && senha.chamada_em) {
      const tempoEspera = calcularDuracao(senha.emitida_em, senha.chamada_em);
      metricas.push({
        label: 'Tempo de Espera',
        valor: tempoEspera,
        icon: <AccessTimeIcon />,
        cor: '#2196f3',
        descricao: 'Tempo entre emissão e chamada'
      });
    }

    // Tempo de Deslocamento (Chamada → Início)
    if (senha.chamada_em && senha.inicio_atendimento) {
      const tempoDeslocamento = calcularDuracao(senha.chamada_em, senha.inicio_atendimento);
      metricas.push({
        label: 'Tempo de Deslocamento',
        valor: tempoDeslocamento,
        icon: <SpeedIcon />,
        cor: '#ff9800',
        descricao: 'Tempo entre chamada e início do atendimento'
      });
    }

    // Tempo de Atendimento (Início → Fim)
    if (senha.inicio_atendimento && senha.finalizada_em) {
      const tempoAtendimento = calcularDuracao(senha.inicio_atendimento, senha.finalizada_em);
      metricas.push({
        label: 'Tempo de Atendimento',
        valor: tempoAtendimento,
        icon: <ScheduleIcon />,
        cor: '#9c27b0',
        descricao: 'Tempo de duração do atendimento'
      });
    }

    // Tempo Total (Emissão → Fim)
    if (senha.emitida_em && senha.finalizada_em) {
      const tempoTotal = calcularDuracao(senha.emitida_em, senha.finalizada_em);
      metricas.push({
        label: 'Tempo Total',
        valor: tempoTotal,
        icon: <TimelineIcon />,
        cor: '#4caf50',
        descricao: 'Tempo total desde emissão até finalização'
      });
    }

    return metricas;
  };

  const timeline = construirTimeline();
  const metricas = calcularMetricas();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          background: customTheme?.palette?.mode === 'dark'
            ? `linear-gradient(135deg, ${alpha(customTheme.palette.grey[900], 0.95)} 0%, ${alpha(customTheme.palette.grey[800], 0.9)} 100%)`
            : 'background.paper',
        }
      }}
    >
      {/* Cabeçalho */}
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${customTheme?.palette?.primary?.main || '#1976d2'} 0%, ${customTheme?.palette?.primary?.dark || '#115293'} 100%)`,
          color: 'white',
          position: 'relative',
          py: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: alpha('#fff', 0.2),
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.2rem',
                width: 56,
                height: 56,
                border: '3px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.2s ease'
                }
              }}
            >
              <TicketIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Detalhes da Senha {senhaParaExibir.senha || senhaParaExibir.numero}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {senhaParaExibir.servico_nome}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={traduzirStatus(senhaParaExibir.status)}
              sx={{
                bgcolor: alpha('#fff', 0.2),
                color: 'white',
                fontWeight: 600,
                borderRadius: 2
              }}
            />
            {senhaParaExibir.preferencial && (
              <Chip
                label="Preferencial"
                sx={{
                  bgcolor: alpha('#fff', 0.15),
                  color: 'white',
                  fontWeight: 600,
                  borderRadius: 2
                }}
              />
            )}
            <IconButton
              onClick={onClose}
              sx={{ 
                color: 'white',
                '&:hover': { bgcolor: alpha('#fff', 0.1) }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      {/* Conteúdo */}
      <DialogContent sx={{ p: 0 }}>
        {carregando && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Carregando detalhes da senha...</Typography>
          </Box>
        )}
        
        {erro && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error" variant="body1">
              {erro}
            </Typography>
            <Button 
              onClick={buscarDetalhesCompletos} 
              sx={{ mt: 2 }}
              variant="outlined"
            >
              Tentar novamente
            </Button>
          </Box>
        )}
        
        {!carregando && !erro && (
          <Grid container>
          {/* Informações Básicas */}
          <Grid item xs={12}>
            <Box sx={{ p: 3, bgcolor: alpha(customTheme?.palette?.primary?.main || '#1976d2', 0.02) }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon color="primary" />
                Informações Básicas
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <BusinessCenterIcon sx={{ fontSize: 32, color: customTheme?.palette?.primary?.main || '#1976d2', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">Serviço</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {senha.servico_nome}
                      </Typography>
                      {senha.servico_alterado && (
                        <Typography variant="caption" color="warning.main">
                          (Alterado de: {senha.servico_inicial})
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <PersonIcon sx={{ fontSize: 32, color: customTheme?.palette?.secondary?.main || '#dc004e', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">Atendente</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {senha.usuario_nome || 'Não atribuído'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h4" sx={{ color: customTheme?.palette?.info?.main || '#0288d1', mb: 1 }}>
                        {senha.guiche || '--'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Guichê</Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Chip
                        label={traduzirStatus(senha.status)}
                        sx={{
                          bgcolor: alpha(getStatusColor(senha.status), 0.1),
                          color: getStatusColor(senha.status),
                          fontWeight: 600,
                          mb: 1
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Métricas de Tempo */}
          {metricas.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon color="primary" />
                  Métricas de Tempo
                </Typography>
                
                <Grid container spacing={2}>
                  {metricas.map((metrica, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          height: '100%', 
                          borderRadius: 2,
                          border: `2px solid ${alpha(metrica.cor, 0.2)}`,
                          '&:hover': {
                            border: `2px solid ${alpha(metrica.cor, 0.4)}`,
                            transform: 'translateY(-2px)',
                            transition: 'all 0.2s ease'
                          },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Box sx={{ color: metrica.cor, mb: 1 }}>
                            {metrica.icon}
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: metrica.cor }}>
                            {metrica.valor}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {metrica.label}
                          </Typography>
                          <Tooltip title={metrica.descricao} placement="bottom">
                            <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                              {metrica.descricao}
                            </Typography>
                          </Tooltip>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          )}

          {/* Timeline */}
          <Grid item xs={12}>
            <Box sx={{ p: 3, bgcolor: alpha(customTheme?.palette?.primary?.main || '#1976d2', 0.02) }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimelineIcon color="primary" />
                Timeline do Atendimento
              </Typography>
              
              {timeline.length > 0 ? (
                <Box sx={{ position: 'relative', pl: 4 }}>
                  {/* Linha vertical da timeline */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '20px',
                      top: '20px',
                      bottom: '20px',
                      width: '4px',
                      background: `linear-gradient(to bottom, ${customTheme?.palette?.primary?.main || '#1976d2'}, ${alpha(customTheme?.palette?.primary?.main || '#1976d2', 0.3)})`,
                      borderRadius: '2px',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-2px',
                        width: '8px',
                        height: '100%',
                        background: `linear-gradient(to bottom, ${alpha(customTheme?.palette?.primary?.main || '#1976d2', 0.1)}, transparent)`,
                        borderRadius: '4px',
                        filter: 'blur(2px)'
                      }
                    }}
                  />
                  
                  {timeline.map((evento, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        mb: index === timeline.length - 1 ? 0 : 4,
                        display: 'flex',
                        alignItems: 'flex-start',
                        animation: `slideInRight 0.6s ease-out ${index * 0.1}s both`,
                        '@keyframes slideInRight': {
                          '0%': {
                            opacity: 0,
                            transform: 'translateX(-30px)'
                          },
                          '100%': {
                            opacity: 1,
                            transform: 'translateX(0)'
                          }
                        }
                      }}
                    >
                      {/* Ícone do evento */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '-32px',
                          top: '8px',
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${evento.color}, ${alpha(evento.color, 0.8)})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          boxShadow: `0 4px 12px ${alpha(evento.color, 0.3)}, 0 0 0 0 ${alpha(evento.color, 0.4)}`,
                          border: `3px solid white`,
                          zIndex: 2,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'scale(1.1)',
                            boxShadow: `0 6px 20px ${alpha(evento.color, 0.4)}, 0 0 0 8px ${alpha(evento.color, 0.1)}`,
                            animation: 'pulse 2s infinite'
                          },
                          '@keyframes pulse': {
                            '0%': {
                              boxShadow: `0 4px 12px ${alpha(evento.color, 0.3)}, 0 0 0 0 ${alpha(evento.color, 0.4)}`
                            },
                            '50%': {
                              boxShadow: `0 6px 20px ${alpha(evento.color, 0.4)}, 0 0 0 12px ${alpha(evento.color, 0.1)}`
                            },
                            '100%': {
                              boxShadow: `0 4px 12px ${alpha(evento.color, 0.3)}, 0 0 0 0 ${alpha(evento.color, 0.4)}`
                            }
                          }
                        }}
                      >
                        {evento.icon}
                      </Box>
                      
                      {/* Card do evento */}
                      <Card
                        sx={{
                          width: '100%',
                          ml: 3,
                          borderRadius: 3,
                          border: evento.isUsuarioEmissao 
                            ? `2px solid ${alpha(evento.color, 0.4)}` 
                            : `2px solid ${alpha(evento.color, 0.2)}`,
                          background: evento.isUsuarioEmissao
                            ? `linear-gradient(135deg, ${alpha(evento.color, 0.08)}, ${alpha(evento.color, 0.15)})`
                            : `linear-gradient(135deg, ${alpha(evento.color, 0.02)}, ${alpha(evento.color, 0.08)})`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          overflow: 'hidden',
                          ...(evento.isUsuarioEmissao && {
                            boxShadow: `0 4px 16px ${alpha(evento.color, 0.2)}`,
                          }),
                          '&:hover': {
                            transform: 'translateY(-4px) translateX(8px)',
                            boxShadow: `0 12px 32px ${alpha(evento.color, 0.2)}`,
                            border: `2px solid ${alpha(evento.color, 0.5)}`,
                            '&::before': {
                              opacity: 1
                            }
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: evento.isUsuarioEmissao ? '6px' : '4px',
                            background: `linear-gradient(90deg, ${evento.color}, ${alpha(evento.color, 0.6)})`,
                            opacity: evento.isUsuarioEmissao ? 1 : 0.7,
                            transition: 'opacity 0.3s ease'
                          }
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontWeight: 700,
                                  color: evento.color,
                                  mb: 0.5
                                }}
                              >
                                {evento.label}
                              </Typography>
                            </Box>
                            <Chip
                              label={`${index + 1}º`}
                              size="small"
                              sx={{
                                bgcolor: alpha(evento.color, 0.1),
                                color: evento.color,
                                fontWeight: 600,
                                minWidth: 35
                              }}
                            />
                          </Box>
                          
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'text.secondary',
                              mb: 2,
                              lineHeight: 1.6
                            }}
                          >
                            {evento.descricao}
                          </Typography>
                          
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 2,
                              bgcolor: alpha(evento.color, 0.05),
                              borderRadius: 2,
                              border: `1px solid ${alpha(evento.color, 0.1)}`
                            }}
                          >
                            <AccessTimeIcon 
                              sx={{ 
                                fontSize: 18, 
                                color: evento.color 
                              }} 
                            />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600,
                                color: evento.color
                              }}
                            >
                              {formatarDataHora(evento.timestamp)}
                            </Typography>
                            {index > 0 && (
                              <>
                                <Box sx={{ flexGrow: 1 }} />
                                <Chip
                                  label={dayjs(evento.timestamp).from(dayjs(timeline[index - 1].timestamp))}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(evento.color, 0.1),
                                    color: evento.color,
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    border: `1px solid ${alpha(evento.color, 0.2)}`,
                                    '& .MuiChip-label': {
                                      px: 1
                                    }
                                  }}
                                />
                              </>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box 
                  sx={{ 
                    textAlign: 'center', 
                    py: 6,
                    bgcolor: alpha(customTheme?.palette?.grey?.[500] || '#757575', 0.05),
                    borderRadius: 3,
                    border: `2px dashed ${alpha(customTheme?.palette?.grey?.[500] || '#757575', 0.2)}`
                  }}
                >
                  <TimelineIcon 
                    sx={{ 
                      fontSize: 48, 
                      color: alpha(customTheme?.palette?.grey?.[500] || '#757575', 0.5),
                      mb: 2
                    }} 
                  />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    Timeline Vazia
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Nenhum evento foi registrado para esta senha
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Serviços Adicionais */}
          {senha.servicos_adicionais && senha.servicos_adicionais.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Serviços Adicionais
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {senha.servicos_adicionais.map((servico, index) => (
                    <Chip
                      key={index}
                      label={servico}
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              </Box>
            </Grid>
          )}
          </Grid>
        )}
      </DialogContent>

      {/* Rodapé */}
      <DialogActions sx={{ p: 3, bgcolor: alpha(customTheme?.palette?.primary?.main || '#1976d2', 0.02) }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: 2,
            px: 4,
            bgcolor: customTheme?.palette?.primary?.main || '#1976d2',
            '&:hover': {
              bgcolor: customTheme?.palette?.primary?.dark || '#115293',
            }
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalDetalhesSenha; 